/** Shapes shared by world settings (settings.music), speaker primitives and the music runtime. */
import type { MusicLoopMode } from '../../utils/musicSync';

export type { MusicLoopMode };

/** One track as a world or speaker references it (enough to play it without a lookup). */
export interface MusicTrackRef {
  /** music_tracks.id (library tracks have fixed ids; see config/musicLibrary.ts). */
  id: string;
  title: string;
  artist?: string;
  url: string;
  /** Seconds. Every client advances the playlist by these, so they must agree. */
  duration: number;
  source: 'library' | 'upload';
  license?: string;
  /** Uploads: when the uploader ticked "I own the rights…" (ISO), and who. */
  attestedAt?: string;
  attestedBy?: string;
}

/** settings.music — the world's non-positional soundtrack. */
export interface WorldMusicSettings {
  tracks: MusicTrackRef[];
  loop: MusicLoopMode;
  shuffle: boolean;
  /** Host volume 0..1 (the listener's own slider multiplies this). */
  volume: number;
  /** Plays for visitors without a host pressing play. */
  autoplay: boolean;
}

/** shape === 'speaker': a positional source placed in the world. */
export interface SpeakerConfig {
  tracks: MusicTrackRef[];
  volume: number;
  /** Metres: full volume close up, silent at this distance. */
  range: number;
  loop: MusicLoopMode;
  shuffle: boolean;
  autoplay: boolean;
}

export const MAX_TRACKS = 50;
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const UPLOAD_TYPES: Record<string, string> = {
  mp3: 'audio/mpeg', ogg: 'audio/ogg', oga: 'audio/ogg', m4a: 'audio/mp4',
};
export const ASSET_HOST_PREFIX = 'https://assets.gathrxr.com/music/';

export const DEFAULT_WORLD_MUSIC: WorldMusicSettings = { tracks: [], loop: 'all', shuffle: false, volume: 0.7, autoplay: true };
export const DEFAULT_SPEAKER: SpeakerConfig = { tracks: [], volume: 0.8, range: 12, loop: 'all', shuffle: false, autoplay: true };

const LOOPS: MusicLoopMode[] = ['all', 'one', 'off'];
const num = (v: unknown, lo: number, hi: number, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');

/** Only our own music folder plays: a host can't point visitors' browsers anywhere else. */
export function isPlayableUrl(url: string): boolean {
  return typeof url === 'string' && url.startsWith(ASSET_HOST_PREFIX) && !url.includes('..');
}

export function parseTrackRef(v: any): MusicTrackRef | null {
  if (!v || typeof v !== 'object') return null;
  const url = str(v.url, 600);
  const id = str(v.id, 80);
  if (!id || !isPlayableUrl(url)) return null;
  return {
    id, url,
    title: str(v.title, 120) || 'Untitled',
    ...(v.artist ? { artist: str(v.artist, 120) } : {}),
    duration: num(v.duration, 0, 7200, 0),
    source: v.source === 'upload' ? 'upload' : 'library',
    ...(v.license ? { license: str(v.license, 200) } : {}),
    ...(v.attestedAt ? { attestedAt: str(v.attestedAt, 40) } : {}),
    ...(v.attestedBy ? { attestedBy: str(v.attestedBy, 40) } : {}),
  };
}

const parseTracks = (v: unknown) => (Array.isArray(v) ? v.map(parseTrackRef).filter(Boolean).slice(0, MAX_TRACKS) as MusicTrackRef[] : []);

/** settings.music from the database (or a peer) → a safe value, or null for none. */
export function parseWorldMusic(v: any): WorldMusicSettings | null {
  if (!v || typeof v !== 'object') return null;
  return {
    tracks: parseTracks(v.tracks),
    loop: LOOPS.includes(v.loop) ? v.loop : 'all',
    shuffle: v.shuffle === true,
    volume: num(v.volume, 0, 1, DEFAULT_WORLD_MUSIC.volume),
    autoplay: v.autoplay !== false,
  };
}

export function parseSpeaker(v: any): SpeakerConfig {
  const o = v && typeof v === 'object' ? v : {};
  return {
    tracks: parseTracks(o.tracks),
    volume: num(o.volume, 0, 1, DEFAULT_SPEAKER.volume),
    range: num(o.range, 2, 60, DEFAULT_SPEAKER.range),
    loop: LOOPS.includes(o.loop) ? o.loop : 'all',
    shuffle: o.shuffle === true,
    autoplay: o.autoplay !== false,
  };
}

/** Key a speaker's shared playback state is stored under. */
export const speakerKey = (primId: string) => `speaker:${primId}`;
export const WORLD_KEY = 'world';
